import { Grid, Paper, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid';
import { useCustomListValues } from 'hooks/useCustomListValues';
import useIsAllowedByRecordType from 'hooks/useIsAllowedByRecordType';
import { useMemo } from 'react';
import { OPTION_FIELD_NAME } from 'ui-component/records/LineItems/constants';
import LineItemsGridSkeleton from 'ui-component/records/LineItems/Grid';
import { useLineItemsData } from 'ui-component/records/LineItems/hooks';
import { getFieldsAsHeadersFields } from 'ui-component/records/LineItems/utils';
import { IRecordField } from 'ui-component/records/types';
import { getRenderCell, getRenderEditCellByType, getValueFormatter } from 'ui-component/records/utils';
import { getListIdsFromHeaders } from 'ui-component/records/utils/headerHelpers';

export type DetailPanelProps = {
    lineItemsRows: Record<string, any>[];
    setLineItemRows: React.Dispatch<React.SetStateAction<Record<string, any>[]>>;
    lineItemsGridRef: React.MutableRefObject<any>;
    recordTypeId: number;
};

export const DetailPanel = ({ lineItemsRows, setLineItemRows, lineItemsGridRef, recordTypeId }: DetailPanelProps) => {
    const { lineItemHeaders, loading: loadingLineItemHeaders } = useLineItemsData(recordTypeId as number);
    const { isAllowed: isAllowedToEditLineItems } = useIsAllowedByRecordType('edit line items', Number(recordTypeId));
    const { isAllowed: isAllowedToExportLineItems } = useIsAllowedByRecordType('export line items', Number(recordTypeId));

    const headerListIds = useMemo(
        () => getListIdsFromHeaders(getFieldsAsHeadersFields(lineItemHeaders?.lineItemsByTypeFileds)),
        [lineItemHeaders?.lineItemsByTypeFileds]
    );
    const { loading: loadingListValues, listValuesByListId } = useCustomListValues(headerListIds);

    const handleProcessCreate = (newOne: any) => {
        const index = lineItemsRows.slice(-1)[0]?.id + 1 || 1;
        setLineItemRows((prev: any) => [...prev, { ...newOne, id: index }]);
        return { ...newOne, id: index };
    };

    const handleProcessUpdate = (newOne: any) => {
        setLineItemRows((prevRows: any) => prevRows.map((row: any) => (row.id === newOne.id ? newOne : row)));
        return newOne;
    };

    const handleDeleteLineItem = (id: any) => {
        setLineItemRows((prevRows: any) => prevRows.filter((row: any) => row.id !== id));
    };

    const handleAddClick = () => {
        lineItemsGridRef.current?.handleAddClick();
    };

    const headerList: GridColDef[] = useMemo(() => {
        if (!lineItemHeaders) return [];
        const sortedArr = [...lineItemHeaders.lineItemsByTypeFileds].sort((a, b) => a.order - b.order);

        return sortedArr.map(
            ({ recordAdditionalFields, order, id }) =>
                ({
                    field: `${recordAdditionalFields.name}-${order}-${id}`,
                    headerName: recordAdditionalFields.name,
                    renderHeader: (params) => (
                        <Typography fontSize="16px" fontWeight={700} color="#000000">
                            {params.colDef.headerName}
                        </Typography>
                    ),
                    editable: true,
                    valueFormatter: getValueFormatter(
                        recordAdditionalFields.name,
                        recordAdditionalFields.dataType,
                        undefined,
                        undefined,
                        recordAdditionalFields.name === OPTION_FIELD_NAME
                    ),
                    renderCell: getRenderCell(
                        recordAdditionalFields.name,
                        undefined,
                        undefined,
                        recordAdditionalFields as unknown as IRecordField,
                        recordAdditionalFields.listType?.id ? listValuesByListId[Number(recordAdditionalFields.listType?.id)] : undefined
                    ),
                    renderEditCell: getRenderEditCellByType(
                        recordAdditionalFields.dataType,
                        recordAdditionalFields.listType?.id,
                        recordAdditionalFields.listType?.id ? listValuesByListId[Number(recordAdditionalFields.listType?.id)] : undefined,
                        recordAdditionalFields.listType?.dataType
                    )
                } as GridColDef)
        );
    }, [listValuesByListId, lineItemHeaders]);

    return (
        <Stack sx={{ display: 'flex', height: '100%', maxWidth: '780px' }}>
            <Paper elevation={1} component={Grid} container sx={{ m: '5px', p: '10px', flexGrow: 1 }}>
                <Grid xs={12} container spacing={1} sx={{ height: '100%' }}>
                    <Grid item xs={12} container spacing={1} height="100%" sx={{ position: 'relative' }}>
                        <LineItemsGridSkeleton
                            gridRef={lineItemsGridRef}
                            handleProcessCreate={handleProcessCreate}
                            handleProcessUpdate={handleProcessUpdate}
                            onDeleteLineItem={handleDeleteLineItem}
                            loadingData={loadingLineItemHeaders || loadingListValues}
                            columns={headerList}
                            rows={lineItemsRows}
                            handleAddClick={handleAddClick}
                            isAllowedToEditLineItems={isAllowedToEditLineItems}
                            isAllowedToExportLineItems={isAllowedToExportLineItems}
                            recordTypeId={recordTypeId}
                            shouldImport={false}
                            shouldShowHistory={false}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Stack>
    );
};
